import React, { useCallback, useEffect, useRef, useState } from 'react';
import { formatPixKey, PixType as FormatPixType } from '../../utils/formatPixKey';
const COUNTDOWN_SECONDS = 60;
export function usePixKeyConfirmationInput({ keyType, keyValue, onConfirm, onResend, disabled = false, loading = false, error, verificationCode, onVerificationCodeChange, }) {
    const [values, setValues] = useState(Array(6).fill(''));
    const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
    const [canResend, setCanResend] = useState(false);
    const [showInlineError, setShowInlineError] = useState(false);
    const [displayError, setDisplayError] = useState(null);
    const inputsRef = useRef(Array(6)
        .fill(null)
        .map(() => React.createRef()));
    useEffect(() => {
        if (verificationCode) {
            const codeArray = verificationCode.split('').slice(0, 6);
            setValues([...codeArray, ...Array(6 - codeArray.length).fill('')]);
        }
    }, [verificationCode]);
    useEffect(() => {
        if (error) {
            setDisplayError(error);
            setShowInlineError(true);
        }
        else {
            setDisplayError(null);
            setShowInlineError(false);
        }
    }, [error]);
    useEffect(() => {
        if (countdown > 0) {
            const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
            return () => clearTimeout(timer);
        }
        else {
            setCanResend(true);
        }
    }, [countdown]);
    const handleChange = useCallback((index, value) => {
        if (value.length > 1)
            return;
        const newValues = [...values];
        newValues[index] = value.toUpperCase();
        setValues(newValues);
        if (onVerificationCodeChange) {
            onVerificationCodeChange(newValues.join(''));
        }
        if (value && index < 5) {
            inputsRef.current[index + 1]?.current?.focus();
        }
    }, [values, onVerificationCodeChange]);
    const handleContinue = useCallback(() => {
        setDisplayError(null);
        setShowInlineError(false);
        onConfirm(values.join(''));
    }, [values, onConfirm]);
    const handleResendCode = useCallback(() => {
        setCanResend(false);
        setCountdown(COUNTDOWN_SECONDS);
        setValues(Array(6).fill(''));
        onResend();
    }, [onResend]);
    const handleKeyDown = useCallback((index, event) => {
        if (event.key === 'Backspace' && !values[index] && index > 0) {
            inputsRef.current[index - 1]?.current?.focus();
        }
    }, [values]);
    const getInstructionText = useCallback(() => {
        if (keyType === 'PHONE') {
            const formattedPhone = formatPixKey(keyValue, FormatPixType.PHONE);
            return { formattedValue: formattedPhone };
        }
        return { formattedValue: keyValue };
    }, [keyType, keyValue]);
    const isComplete = values.join('').length === 6;
    const inputIds = ['a', 'b', 'c', 'd', 'e', 'f'];
    return {
        values,
        countdown,
        canResend,
        showInlineError,
        displayError,
        inputsRef,
        isComplete,
        inputIds,
        handleChange,
        handleContinue,
        handleResendCode,
        handleKeyDown,
        getInstructionText,
        isDisabled: disabled || loading,
    };
}
